export type RewardsCurrency = "CNY" | "USDT" | null;
export type RebateTier = { tier: string; turnover: number; ratePercent: number };
export type RebateProvider = { provider: "PG" | "PP"; name: string; category: "slots"; tiers: RebateTier[] };
export type VipTier = { level: number; upgradeTurnover: number; upgradeDeposit: number; retainTurnover: number; retainDeposit: number; reward: number };

export type ActiveVipPolicy = {
  readonly version: number;
  readonly activatedAt: string;
  readonly effectiveAt: string;
  readonly currency: "CNY";
  readonly timezone: "Asia/Shanghai";
  readonly windowDays: 30;
  readonly depositPeriod: "rolling-30-days";
  readonly downgradeSchedule: "daily";
  readonly skippedLevelRewards: "all-reached";
  readonly claimMode: "manual" | "automatic" | null;
  readonly rewardOncePerLevel: true;
  /** Level evaluation is active, but wallet/reward payout remains a separate disabled capability. */
  readonly rewardPayoutEnabled: false;
  readonly levels: readonly VipTier[];
};

export type MemberVipProgress = {
  level: number;
  turnover: { current: string; required: string; percent: number };
  deposit: { current: string; required: string; percent: number };
  percent: number;
};

export type MemberVipDecision = "initialized" | "upgraded" | "retained" | "downgraded";

/** Server-owned state. A member level is a durable daily decision, not a value re-derived on every page load. */
export type StoredMemberVipState = {
  readonly version: 1;
  readonly userId: string;
  policyVersion: number;
  policyEffectiveAt: string;
  currentLevel: number;
  lastEvaluatedDay: string | null;
  lastEvaluatedAt: string | null;
  levelChangedAt: string | null;
  lastDecision: MemberVipDecision | null;
};

export type MemberVipLedger = {
  readonly version: 1;
  members: Record<string, StoredMemberVipState>;
};

export type MemberVipStatus = {
  status: "inactive" | "collecting" | "active";
  currentLevel: number;
  windowTurnover: string;
  windowDeposit: string;
  nextLevel: MemberVipProgress | null;
  policyVersion: number | null;
  effectiveAt: string | null;
  /** Time the persisted level was last decided. Metrics may be newer than this value. */
  lastEvaluatedAt: string | null;
  nextEvaluationAt: string | null;
  levelChangedAt: string | null;
  lastDecision: MemberVipDecision | null;
  evaluatedAt: string;
  window: { from: string | null; through: string; coverageFrom: string | null; coverageThrough: string | null };
  rewardPayoutEnabled: false;
};

/** Editable next-version rules plus a server-owned immutable active VIP snapshot. */
export type RewardsDraft = {
  version: number;
  /** Legacy aggregate state remains the rebate state; VIP activation is represented inside vip. */
  status: "draft";
  enabled: false;
  updatedAt: string | null;
  currency: RewardsCurrency;
  timezone: "Asia/Shanghai" | "UTC" | null;
  rebate: {
    status: "draft";
    enabled: false;
    period: "day" | "month" | "lifetime" | null;
    thresholdMeaning: "minimum" | "maximum" | null;
    calculation: "whole-period" | "progressive" | null;
    belowFirstTier: "zero" | "first-tier" | null;
    eligibleBets: "settled-valid-turnover" | null;
    claimMode: "manual" | "automatic" | null;
    providers: RebateProvider[];
  };
  vip: {
    status: "draft" | "active";
    enabled: boolean;
    rewardPayoutEnabled: false;
    activePolicy: ActiveVipPolicy | null;
    windowDays: 30;
    depositPeriod: "rolling-30-days" | "lifetime" | null;
    downgradeSchedule: "daily" | "monthly" | null;
    skippedLevelRewards: "all-reached" | "highest-only" | null;
    claimMode: "manual" | "automatic" | null;
    rewardOncePerLevel: true;
    levels: VipTier[];
  };
};

export const rewardsCurrencyLabel = (currency: RewardsCurrency) => currency === "CNY" ? "人民币 CNY" : currency === "USDT" ? "USDT" : "币种待确认";

export const rewardPolicyOptions = {
  timezone: [{ value: "Asia/Shanghai", label: "北京时间" }, { value: "UTC", label: "UTC" }],
  period: [{ value: "day", label: "每日" }, { value: "month", label: "每月" }, { value: "lifetime", label: "累计" }],
  thresholdMeaning: [{ value: "minimum", label: "达到该下限进入本档" }, { value: "maximum", label: "该数值为本档上限" }],
  calculation: [{ value: "whole-period", label: "当前档比例计算整个周期" }, { value: "progressive", label: "按阶梯分段计算" }],
  belowFirstTier: [{ value: "zero", label: "不计算返水" }, { value: "first-tier", label: "使用第一档比例" }],
  eligibleBets: [{ value: "settled-valid-turnover", label: "供应商已结算注单的有效投注" }],
  claimMode: [{ value: "manual", label: "会员手动领取" }, { value: "automatic", label: "自动发放" }],
  depositPeriod: [{ value: "rolling-30-days", label: "近 30 天充值" }, { value: "lifetime", label: "累计充值" }],
  downgradeSchedule: [{ value: "daily", label: "每天检查" }, { value: "monthly", label: "每月检查" }],
  skippedLevelRewards: [{ value: "all-reached", label: "补发所有达到等级" }, { value: "highest-only", label: "仅最高达到等级" }],
} as const;

export function rewardsPolicyLabel(options: readonly { value: string; label: string }[], value: string | null) {
  return options.find(option => option.value === value)?.label ?? "待确认";
}
